import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Search, ScanBarcode, Truck, User, Wrench, Camera, X, Check, ArrowLeft, Minus, Plus } from "lucide-react";

export default function ScaricoMobilePage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1); // 1=search, 2=qty, 3=dest, 4=details
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [destTipo, setDestTipo] = useState(""); // cliente | officina_mobile | interno
  const [officine, setOfficine] = useState([]);
  const [form, setForm] = useState({
    officina_mobile_id: "", targa: "", telaio: "", matricola: "",
    macchina: "", cliente: "", commessa: "", note: "", foto: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/officine-mobili").then(r => setOfficine(r.data)); }, []);

  const doSearch = async (q) => {
    if (!q || q.length < 1) { setResults([]); return; }
    const { data } = await api.get("/products", { params: { q } });
    setResults(data.slice(0, 10));
  };
  const pick = (p) => { setProduct(p); setStep(2); setQty(1); };

  const onPhoto = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 1.5 * 1024 * 1024) { toast.error("Foto troppo grande (max 1.5MB)"); return; }
    const r = new FileReader(); r.onload = () => setForm(s=>({...s, foto: r.result})); r.readAsDataURL(f);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const causaleMap = { cliente: "Sostituzione in cantiere", officina_mobile: "Trasferimento officina mobile", interno: "Officina interna" };
      const { data } = await api.post("/movements", {
        product_id: product.id, tipo: "scarico", quantita: qty,
        causale: causaleMap[destTipo], destinazione_tipo: destTipo,
        officina_mobile_id: destTipo === "officina_mobile" ? form.officina_mobile_id : "",
        targa: form.targa, telaio: form.telaio, matricola: form.matricola,
        macchina: form.macchina, cliente: form.cliente, commessa: form.commessa,
        tecnico: user.name, note: form.note, foto: form.foto,
      });
      toast.success(`Scarico OK. Giacenza: ${data.giacenza_dopo}`);
      nav("/movimenti");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#050608] pb-24">
      {/* Step header */}
      <div className="sticky top-14 lg:top-0 z-10 bg-[#050608]/95 backdrop-blur border-b border-zinc-800 p-4">
        <div className="flex items-center gap-3">
          {step > 1 && <button onClick={()=>setStep(step-1)} className="p-2 -ml-2"><ArrowLeft size={20}/></button>}
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Step {step}/4 — Scarico Mobile</div>
            <div className="font-display text-xl font-black uppercase">
              {step===1 && "Cerca Ricambio"}
              {step===2 && "Quantità"}
              {step===3 && "Destinazione"}
              {step===4 && "Conferma"}
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 max-w-2xl mx-auto">
        {step === 1 && (
          <>
            <div className="gm-card p-4 mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={20} />
                <input data-testid="mobile-scarico-search" autoFocus value={query} onChange={(e)=>{setQuery(e.target.value); doSearch(e.target.value);}}
                  placeholder="Cerca codice o descrizione" className="gm-input pl-11 text-lg" />
              </div>
              <button onClick={()=>nav("/scanner?dest=scarico")} className="mt-3 w-full h-14 border-2 border-[#4CAF50] text-[#4CAF50] uppercase tracking-widest font-bold flex items-center justify-center gap-2 rounded-sm" data-testid="mobile-scan-trigger">
                <ScanBarcode size={22} /> Scansiona Barcode/QR
              </button>
            </div>
            <div className="space-y-2">
              {results.map(p => (
                <button key={p.id} onClick={()=>pick(p)} disabled={p.giacenza<=0}
                  data-testid="mobile-product-card"
                  className={`w-full gm-card p-4 text-left flex items-center justify-between gap-3 ${p.giacenza<=0?"opacity-50":"active:bg-zinc-800"}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-mono-data text-xs text-[#4CAF50]">{p.codice_interno}</div>
                    <div className="font-semibold text-base truncate">{p.descrizione}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">{p.marca} • {p.posizione || "—"}</div>
                  </div>
                  <div className="text-right">
                    <div className={`font-display text-3xl font-black ${p.giacenza===0?"text-[#FF3B30]":"text-white"}`}>{p.giacenza}</div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">disp.</div>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}

        {step === 2 && product && (
          <div className="gm-card p-5">
            <div className="border-l-4 border-[#4CAF50] pl-3 mb-5">
              <div className="font-mono-data text-xs text-[#4CAF50]">{product.codice_interno}</div>
              <div className="font-semibold">{product.descrizione}</div>
              <div className="text-xs text-zinc-500">Disponibili: <b className="text-white">{product.giacenza}</b></div>
            </div>

            <label className="block text-xs font-bold uppercase tracking-widest text-zinc-400 mb-3">Quantità da scaricare</label>
            <div className="flex items-center gap-3 justify-center mb-6">
              <button onClick={()=>setQty(Math.max(1, qty-1))} className="h-16 w-16 bg-zinc-900 border border-zinc-700 rounded-sm flex items-center justify-center text-white active:bg-zinc-800" data-testid="qty-minus"><Minus size={24}/></button>
              <input type="number" value={qty} onChange={(e)=>setQty(Math.max(1, Math.min(product.giacenza, parseInt(e.target.value)||1)))} className="h-16 w-32 text-center font-display text-5xl font-black bg-transparent border-2 border-zinc-700 focus:border-[#4CAF50] outline-none rounded-sm" data-testid="qty-input" />
              <button onClick={()=>setQty(Math.min(product.giacenza, qty+1))} className="h-16 w-16 bg-zinc-900 border border-zinc-700 rounded-sm flex items-center justify-center text-white active:bg-zinc-800" data-testid="qty-plus"><Plus size={24}/></button>
            </div>

            <button onClick={()=>setStep(3)} disabled={qty<1||qty>product.giacenza} className="gm-btn-primary w-full h-16 rounded-sm text-lg" data-testid="mobile-step-next-qty">
              Avanti →
            </button>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3">
            <DestCard testId="dest-cliente" active={destTipo==="cliente"} onClick={()=>{setDestTipo("cliente"); setStep(4);}} icon={User} title="Cliente / Macchina" desc="Sostituzione su mezzo del cliente in cantiere" />
            <DestCard testId="dest-officina-mobile" active={destTipo==="officina_mobile"} onClick={()=>{setDestTipo("officina_mobile"); setStep(4);}} icon={Truck} title="Officina Mobile" desc="Trasferisci sul furgone (rimane in giacenza)" />
            <DestCard testId="dest-interno" active={destTipo==="interno"} onClick={()=>{setDestTipo("interno"); setStep(4);}} icon={Wrench} title="Uso Interno / Officina" desc="Riparazioni interne o consumo generico" />
          </div>
        )}

        {step === 4 && (
          <div className="gm-card p-5 space-y-4">
            {destTipo === "cliente" && (
              <>
                <BigField label="Cliente" testId="dest-cliente-input" value={form.cliente} onChange={v=>setForm(s=>({...s, cliente:v}))} />
                <BigField label="Macchina" placeholder="es. Epiroc SB552" value={form.macchina} onChange={v=>setForm(s=>({...s, macchina:v}))} />
                <BigField label="Targa" value={form.targa} onChange={v=>setForm(s=>({...s, targa:v}))} mono />
                <BigField label="Matricola / Telaio" value={form.matricola} onChange={v=>setForm(s=>({...s, matricola:v}))} mono />
                <BigField label="Commessa / Intervento" value={form.commessa} onChange={v=>setForm(s=>({...s, commessa:v}))} />
              </>
            )}
            {destTipo === "officina_mobile" && (
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">Seleziona furgone *</label>
                <select required value={form.officina_mobile_id} onChange={e=>setForm(s=>({...s, officina_mobile_id: e.target.value}))} className="gm-input text-base h-14" data-testid="officina-select">
                  <option value="">— scegli —</option>
                  {officine.map(o => <option key={o.id} value={o.id}>{o.nome} {o.targa?`(${o.targa})`:""} {o.assegnato_a?`— ${o.assegnato_a}`:""}</option>)}
                </select>
              </div>
            )}
            {destTipo === "interno" && (
              <BigField label="Descrizione utilizzo" value={form.note} onChange={v=>setForm(s=>({...s, note:v}))} placeholder="es. manutenzione bobcat magazzino" />
            )}

            <BigField label="Note (opzionale)" value={form.note} onChange={v=>setForm(s=>({...s, note:v}))} />

            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">Foto (opzionale)</label>
              {form.foto ? (
                <div className="relative inline-block">
                  <img src={form.foto} className="h-32 object-cover border border-zinc-800" alt="" />
                  <button onClick={()=>setForm(s=>({...s, foto:""}))} className="absolute -top-2 -right-2 bg-[#FF3B30] text-white p-1 rounded-full"><X size={14}/></button>
                </div>
              ) : (
                <label className="flex items-center justify-center gap-2 h-14 border-2 border-dashed border-zinc-700 active:border-[#4CAF50] cursor-pointer" data-testid="mobile-photo-input">
                  <Camera size={20} className="text-zinc-400" />
                  <span className="text-sm uppercase tracking-widest text-zinc-400">Scatta foto</span>
                  <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onPhoto} />
                </label>
              )}
            </div>

            <div className="border-t border-zinc-800 pt-4 mt-4">
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Riepilogo</div>
              <div className="text-sm space-y-1">
                <div>Ricambio: <b>{product.descrizione}</b> ({product.codice_interno})</div>
                <div>Quantità: <b className="text-[#4CAF50]">{qty}</b></div>
                <div>Destinazione: <b className="uppercase">{destTipo.replace("_"," ")}</b></div>
                <div>Meccanico: <b>{user.name}</b></div>
              </div>
            </div>

            <button onClick={submit} disabled={busy || (destTipo==="officina_mobile" && !form.officina_mobile_id)}
              className="gm-btn-primary w-full h-16 rounded-sm text-lg flex items-center justify-center gap-2" data-testid="mobile-scarico-confirm">
              <Check size={22} /> {busy ? "Salvataggio..." : "Conferma Scarico"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const DestCard = ({active, onClick, icon: Icon, title, desc, testId}) => (
  <button data-testid={testId} onClick={onClick}
    className={`w-full p-5 border-2 text-left flex items-center gap-4 active:bg-zinc-900 transition-colors ${active?"border-[#4CAF50] bg-[#4CAF50]/5":"border-zinc-700"}`}>
    <div className={`h-14 w-14 flex items-center justify-center ${active?"bg-[#4CAF50] text-white":"bg-zinc-900 text-zinc-400"}`}>
      <Icon size={26} />
    </div>
    <div className="flex-1">
      <div className="font-display text-xl font-bold uppercase tracking-tight">{title}</div>
      <div className="text-sm text-zinc-400">{desc}</div>
    </div>
  </button>
);

const BigField = ({label, value, onChange, placeholder="", mono=false, testId}) => (
  <div data-testid={testId}>
    <label className="block text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">{label}</label>
    <input value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder}
      className={`gm-input h-14 text-base ${mono?"font-mono-data":""}`} />
  </div>
);
